import { queryOptions, useMutation } from '@tanstack/react-query'
import z from 'zod'

import {
  ActivityType,
  ActivityVisibility,
  ActorType,
  FeedGroupName,
  FeedGroupType,
  UserUnreadActivitiesCountResponse,
} from '@forto-dev/feed-v2-models'

import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { User } from '@/features/auth'

import { APIFeed, Feed, FeedSchema } from '../domain/feed'
import { UnreadActivitiesCountList, UnreadActivitiesCountListSchema } from '../domain/userInteraction'

const basePath = '/api/feed-v2'

export type EntityFeedPageParams = {
  orderBy?: string
  orderByDesc: string
  page: number
  pageSize: number
  entityID: string[]
  activityTypes?: ActivityType[]
}

type CreateFeedActivityParams = {
  entityId: string
  user?: User
  data: {
    content: string
  }
}

export type CreateFeedActivityResult = {
  activitySent: boolean
}

export const activityFeedConfig: ApiConfig<{
  getEntityFeed: EndpointConfig<{ pageParam: EntityFeedPageParams }, APIFeed, Feed>
  createV2ShipmentFeedActivity: EndpointConfig<CreateFeedActivityParams, void>
  getFeedUnreadCount: EndpointConfig<
    { entityIDs: string[] },
    UserUnreadActivitiesCountResponse,
    UnreadActivitiesCountList
  >
  saveLastReadActivity: EndpointConfig<{ entityID: string; activityID: string }, void>
}> = {
  getEntityFeed: {
    requestConfig: ({ pageParam }) => ({
      endpoint: `${basePath}/feed`,
      method: 'get',
      deserializationStrategy: FeedSchema,
      options: {
        params: Object.fromEntries(Object.entries(pageParam).filter(([, value]) => typeof value !== 'undefined')),
      },
    }),
  },
  createV2ShipmentFeedActivity: {
    requestConfig: ({ entityId, user, data }) => ({
      endpoint: `${basePath}/activities`,
      method: 'post',
      body: {
        activity: {
          actor: {
            type: ActorType.Person,
            id: user?.id,
            fullName: user ? `${user?.firstName} ${user?.lastName}` : undefined,
            email: user?.email,
          },
          type: ActivityType.MessageFeedV1,
          object: {
            id: entityId,
            type: ActivityType.MessageFeedV1,
          },
          customField: {
            feedV1Activity: {
              content: data.content,
              referenceId: entityId,
              userAuthor_id: user?.id,
              type: ActivityType.MessageFeedV1,
            },
          },
        },
        feedInfo: {
          customerID: user?.customerId,
          entityID: entityId,
          entityType: entityId.startsWith('B') ? FeedGroupName.booking : FeedGroupName.shipment,
          activityVisibility: ActivityVisibility.public,
          groupType: FeedGroupType.flat,
        },
      },
    }),
  },
  getFeedUnreadCount: {
    requestConfig: ({ entityIDs }) => {
      return {
        endpoint: `${basePath}/userInteractions/unread-count`,
        method: 'get',
        options: {
          params: {
            entityID: entityIDs,
          },
        },
        deserializationStrategy: z.preprocess((data) => {
          if (Array.isArray(data)) {
            return data.reduce((acc, item) => {
              return {
                ...acc,
                [item.entityID]: item,
              }
            }, {})
          }
        }, UnreadActivitiesCountListSchema),
      }
    },
  },
  saveLastReadActivity: {
    requestConfig: ({ entityID, activityID }) => ({
      endpoint: `${basePath}/userInteractions/${entityID}/last-read/${activityID}`,
      method: 'post',
    }),
  },
}

export const activityFeedClient = clientFactory(activityFeedConfig)

export const GET_FEED_UNREAD_COUNT_QUERY_KEY = 'feedUnreadCount'

export const getFeedUnreadCountQueryOptions = (entityIDs: string[], enabled = true) => {
  return queryOptions({
    queryKey: [GET_FEED_UNREAD_COUNT_QUERY_KEY, entityIDs],
    queryFn: () => activityFeedClient.getFeedUnreadCount({ entityIDs }),
    enabled: entityIDs.length > 0 && enabled,
  })
}

export const useSaveLastReadActivity = () => {
  return useMutation({
    mutationFn: activityFeedClient.saveLastReadActivity,
  })
}
